import type * as Theme from '#theme'
import { computed, useCookie, useCoreConfig } from '#imports'
import type { ComputedRef, WritableComputedRef } from '#imports'

export type ColorSchemeMode = 'light' | 'dark'

export const COLOR_SCHEME_COOKIE = 'color-scheme'

export interface UseColorSchemeReturn {
    /** Whether the site opted in via `core.theme.colorScheme`. Everything below is inert when false. */
    isEnabled: ComputedRef<boolean>
    /** The player's selected mode. Writing it persists the choice. */
    mode: WritableComputedRef<ColorSchemeMode>
    /**
     * The theme the selected mode maps to, or `undefined` until the player has
     * actually chosen — the site's own `defaultTheme` stands until then.
     */
    themeName: ComputedRef<Theme.Name | undefined>
    setMode: (mode: ColorSchemeMode) => void
    toggle: () => void
}

/**
 * The player's light/dark preference for sites that configure
 * `core.theme.colorScheme`.
 *
 * Backed by a cookie rather than local storage so the server resolves the same
 * mode the client will: `UIApp` feeds `themeName` into the global
 * `ThemeProvider`, which writes the `<body>` class during SSR, and a mismatch
 * would flash the wrong theme on every first paint.
 *
 * Players who have never chosen keep `light`. The OS `prefers-color-scheme` is
 * deliberately not consulted — it would flip existing players' sites without
 * them asking.
 *
 * Intentionally a plain composable, not `createSharedComposable`: all state
 * lives in the request-scoped cookie, and memoising it across SSR requests
 * would leak one player's mode into another's response.
 */
export const useColorScheme = (): UseColorSchemeReturn => {
    const { theme: config } = useCoreConfig()
    const colorScheme = config.colorScheme

    const cookie = useCookie<ColorSchemeMode | null>(COLOR_SCHEME_COOKIE, {
        default: () => null,
        sameSite: 'lax',
        maxAge: 60 * 60 * 24 * 365,
    })

    const isEnabled = computed(() => Boolean(colorScheme))

    const mode = computed<ColorSchemeMode>({
        get() {
            return cookie.value === 'dark' ? 'dark' : 'light'
        },
        set(value) {
            if (!colorScheme) return

            cookie.value = value
        },
    })

    // Only an explicit choice overrides the theme. Without this a site that
    // inherits `colorScheme` from a layer it merely extends would have every
    // page repainted in the layer's brand, outranking its own `defaultTheme`.
    const themeName = computed(() => {
        if (!colorScheme || cookie.value === null) return undefined

        return colorScheme[mode.value]
    })

    function setMode(value: ColorSchemeMode) {
        mode.value = value
    }

    function toggle() {
        setMode(mode.value === 'dark' ? 'light' : 'dark')
    }

    return { isEnabled, mode, themeName, setMode, toggle }
}
